import React, { useEffect, useMemo, useRef, useState } from "react";
import SummaryCards from "../../components/Common/SummaryCards";
import {
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
} from "../../components/Common/AppOverlay";
import * as Yup from "yup";
import { useFormik } from "formik";
import { showLoading } from "react-global-loading";
import { SuccessNotify, ErrorNotify } from "../../shared/Alert";
import CustomDataGrid from "../../components/Common/CustomDataGrid";
import { driver } from "driver.js";
import ActionMenu from "../../components/Common/ActionMenu";
import useAdminDataLifecycle from "../../components/Common/useAdminDataLifecycle";
import "driver.js/dist/driver.css";
import "../Standards/Standards.scss";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : date.toLocaleDateString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      });
};

const statusInfo = (status) =>
  status === 1
    ? { label: "Active", tone: "active" }
    : { label: "Inactive", tone: "archived" };

const MasterActionMenu = ({ master, onEdit, lifecycleActions }) => {
  return (
    <ActionMenu
      label={master.name}
      triggerClassName="masters-grid-action"
      actions={[
        {
          label: "Edit details",
          icon: <i className="bx bx-edit-alt" />,
          onSelect: () => onEdit(master),
        },
        ...lifecycleActions,
      ]}
    />
  );
};

const lifecycleTypes = {
  "review-types": "review-type",
  "work-types": "work-type",
  "client-types": "client-type",
  "industry-types": "industry-type",
};

const MasterList = ({
  createRequest = 0,
  tourRequest = 0,
  label,
  pluralLabel,
  listMasters,
  createMaster,
  fileName,
  description,
}) => {
  const [masters, setMasters] = useState([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [editingMaster, setEditingMaster] = useState(null);
  const guidedTourRef = useRef(null);
  const lastTourRequest = useRef(0);

  const loadData = () => {
    showLoading(true);
    listMasters()
      .then((response) => {
        setMasters(response?.data || []);
      })
      .catch((error) => {
        console.error(error);
        ErrorNotify(
          `Error occurred while fetching ${pluralLabel.toLowerCase()}.`
        );
      })
      .finally(() => showLoading(false));
  };
  const lifecycle = useAdminDataLifecycle(loadData);

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingMaster(null);
    loadData();
  };

  const openMasterDetail = (master = null) => {
    setEditingMaster(master);
    setIsDrawerOpen(true);
  };
  useEffect(() => {
    loadData();
  }, []);
  useEffect(() => {
    if (createRequest && createMaster) openMasterDetail();
  }, [createRequest, createMaster]);
  useEffect(() => () => guidedTourRef.current?.destroy(), []);

  const startGuidedTour = () => {
    guidedTourRef.current?.destroy();
    const guidedTour = driver({
      animate: true,
      allowClose: true,
      overlayColor: "#102451",
      overlayOpacity: 0.48,
      popoverClass: "masters-tour-popover",
      showProgress: true,
      progressText: "Step {{current}} of {{total}}",
      nextBtnText: "Next →",
      prevBtnText: "← Previous",
      doneBtnText: "Finish",
      smoothScroll: true,
      stagePadding: 8,
      stageRadius: 8,
      onDestroyed: () => {
        guidedTourRef.current = null;
      },
      steps: [
        {
          element: `[data-master-tour="${fileName}-overview"]`,
          popover: {
            title: `${pluralLabel} overview`,
            description: `Review the number and status of configured ${pluralLabel.toLowerCase()}.`,
            side: "bottom",
            align: "start",
          },
        },
        {
          element: `[data-master-tour="${fileName}-filters"]`,
          popover: {
            title: "Find records",
            description: `Search and filter the ${pluralLabel.toLowerCase()} list.`,
            side: "bottom",
            align: "start",
          },
        },
        {
          element: `[data-master-tour="${fileName}-table"]`,
          popover: {
            title: `Manage ${pluralLabel}`,
            description:
              "Review details, status, usage, and the most recent updates in one place.",
            side: "top",
            align: "start",
          },
        },
      ],
    });
    guidedTourRef.current = guidedTour;
    guidedTour.drive();
  };

  useEffect(() => {
    if (tourRequest > lastTourRequest.current) startGuidedTour();
    lastTourRequest.current = tourRequest;
  }, [tourRequest]);

  const counts = useMemo(
    () => ({
      total: masters.length,
      active: masters.filter((item) => item.status === 1).length,
      inactive: masters.filter((item) => item.status !== 1).length,
      documented: masters.filter((item) => item.description).length,
    }),
    [masters]
  );
  const filteredMasters = useMemo(
    () =>
      masters.filter(
        (item) =>
          `${item.name || ""} ${item.description || ""}`
            .toLowerCase()
            .includes(search.toLowerCase()) &&
          (status === "all" || String(item.status) === status)
      ),
    [masters, search, status]
  );
  const columns = [
    {
      id: "name",
      header: "Name",
      accessorKey: "name",
      minSize: 210,
      flex: 1.1,
      cell: ({ getValue }) => (
        <div className="standards-grid__name" style={{ fontSize: 13 }}>
          <span>{(getValue() || "M").charAt(0)}</span>
          {getValue()}
        </div>
      ),
    },
    {
      id: "description",
      header: "Description",
      accessorKey: "description",
      minSize: 360,
      flex: 2,
      cell: ({ getValue }) => (
        <span className="standards-grid__description">{getValue() || "—"}</span>
      ),
    },
    {
      id: "status",
      header: "Status",
      accessorFn: (row) => statusInfo(row.status).label,
      minSize: 120,
      flex: 0.7,
      cell: ({ row, getValue }) => (
        <span
          className={`standards-grid__status standards-grid__status--${
            statusInfo(row.original.status).tone
          }`}
        >
          {getValue()}
        </span>
      ),
    },
    {
      id: "updatedOn",
      header: "Last Updated",
      accessorFn: (row) => formatDate(row.updatedOn),
      minSize: 155,
      flex: 0.85,
    },
    {
      id: "actions",
      header: "Actions",
      accessorFn: () => "",
      enableSorting: false,
      enableColumnFilter: false,
      enableExport: false,
      minSize: 86,
      flex: 0,
      cell: ({ row }) => (
        <MasterActionMenu
          master={row.original}
          onEdit={openMasterDetail}
          lifecycleActions={
            lifecycleTypes[fileName]
              ? lifecycle.actions(lifecycleTypes[fileName], row.original)
              : []
          }
        />
      ),
    },
  ];

  return (
    <section className="standards-page__content masters-list">
      <header className="masters-list__header">
        <div>
          <h1 className="piers-page-title">
            {pluralLabel} <i className="bx bx-info-circle" />
          </h1>
          <p>
            {description ||
              `${pluralLabel} are used to categorize and organize records during engagement reviews.`}
          </p>
        </div>
      </header>
      <SummaryCards
        items={[
          [
            "bx-category",
            "blue",
            `Total ${pluralLabel}`,
            counts.total,
            `All configured ${pluralLabel.toLowerCase()}`,
          ],
          [
            "bx-check-circle",
            "green",
            `Active ${pluralLabel}`,
            counts.active,
            `${
              counts.total
                ? Math.round((counts.active / counts.total) * 100)
                : 0
            }% of total`,
          ],
          [
            "bx-pause-circle",
            "orange",
            `Inactive ${pluralLabel}`,
            counts.inactive,
            `${
              counts.total
                ? Math.round((counts.inactive / counts.total) * 100)
                : 0
            }% of total`,
          ],
          [
            "bx-detail",
            "purple",
            "Descriptions Completed",
            counts.total
              ? `${Math.round((counts.documented / counts.total) * 100)}%`
              : "0%",
            `Descriptions for ${pluralLabel.toLowerCase()}`,
          ],
        ]}
        ariaLabel="Page summary"
        dataAttributes={{ "data-master-tour": `${fileName}-overview` }}
      />
      <section className="standards-grid-panel">
        
        <div data-master-tour={`${fileName}-table`}>
          <CustomDataGrid
 filterContent={<section
          className="standards-filter-panel"
          data-master-tour={`${fileName}-filters`}
          aria-label={`${pluralLabel} filters`}
        >
          <div className="standards-filter-panel__fields">
            <label>
              <span>Search</span>
              <div className="standards-filter-panel__search">
                <i className="bx bx-search" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder={`Search ${pluralLabel.toLowerCase()}...`}
                />
              </div>
            </label>
            <label>
              <span>Status</span>
              <select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </label>
          </div>
          <div className="standards-filter-panel__actions">
            <button
              type="button"
              onClick={() => {
                setSearch("");
                setStatus("all");
              }}
            >
              <i className="bx bx-filter-alt" /> Clear Filters
            </button>
          </div>
        </section>}

            data={filteredMasters}
            columns={columns}
            onRefresh={loadData}
            fileName={fileName}
            recordLabel={pluralLabel.toLowerCase()}
            showToolbar={false}
            initialPageSize={15}
            rowHeight={56}
            columnHeaderHeight={56}
            gridSx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#ffffff !important",
                borderBottom: "1px solid #e7ebf2",
              },
              "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader--sortable":
                { backgroundColor: "#ffffff !important", color: "#52627f" },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#52627f",
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: ".2px",
              },
              "& .MuiDataGrid-columnSeparator": { color: "#e7ebf2" },
              "& .MuiDataGrid-cell": {
                fontSize: 13,
                borderBottomColor: "#edf0f5",
                color: "#273957",
              },
              "& .MuiDataGrid-row": { minHeight: "56px !important" },
              "& .MuiDataGrid-row:hover": { backgroundColor: "#f8faff" },
              "& .MuiDataGrid-footerContainer, & .MuiDataGrid-footerContainer .MuiTypography-root":
                { fontSize: 14, borderTopColor: "#e7ebf2" },
            }}
          />
        </div>
      </section>
      {createMaster && (
        <MasterDetail
          label={label}
          createMaster={createMaster}
          master={editingMaster}
          isOpen={isDrawerOpen}
          onClose={closeDrawer}
        />
      )}
      {lifecycle.dialog}
    </section>
  );
};

const MasterDetail = ({ label, createMaster, master, isOpen, onClose }) => {
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: master?.id || 0,
      name: master?.name || "",
      description: master?.description || "",
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required(`Enter a ${label.toLowerCase()}`),
    }),
    onSubmit: (values) => {
      showLoading(true);
      createMaster({
        id: values.id,
        name: values.name.trim(),
        description: values.description?.trim() || "",
      })
        .then((response) => {
          if (response.status) {
            SuccessNotify(
              `${label} ${values.id ? "updated" : "created"} successfully.`
            );
            validation.resetForm();
            onClose();
          } else ErrorNotify(response.message);
        })
        .catch((error) => {
          console.error(error);
          ErrorNotify(`Unable to save the ${label.toLowerCase()}.`);
        })
        .finally(() => showLoading(false));
    },
  });
  const isEditing = Boolean(master?.id);
  return (
    <Offcanvas
      backdrop="static"
      className="standards-drawer"
      direction="end"
      isOpen={isOpen}
      toggle={onClose}
    >
      <OffcanvasHeader toggle={onClose} className="canvasHeader">
        {isEditing ? "Edit" : "New"} {label}
      </OffcanvasHeader>
      <OffcanvasBody>
        <Form
          className="standard-drawer-form"
          onSubmit={(event) => {
            event.preventDefault();
            validation.handleSubmit();
          }}
        >
          <div className="standard-drawer-form__content">
            <p className="standard-drawer-form__intro">
              {isEditing
                ? `Update this ${label.toLowerCase()}.`
                : `Add a ${label.toLowerCase()} for use across the platform.`}
            </p>
            <section className="standard-drawer-form__section">
              <div className="standard-drawer-form__section-title">
                <span>
                  <i className="bx bx-category" />
                  {label} Information
                </span>
                <i className="bx bx-chevron-down" />
              </div>
              <FormGroup>
                <Label htmlFor="master-name">
                  Name <b>*</b>
                </Label>
                <Input
                  id="master-name"
                  name="name"
                  placeholder={`Enter ${label.toLowerCase()}`}
                  value={validation.values.name}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  invalid={Boolean(
                    validation.touched.name && validation.errors.name
                  )}
                />
                {validation.touched.name && validation.errors.name && (
                  <FormFeedback>{validation.errors.name}</FormFeedback>
                )}
              </FormGroup>
              <FormGroup>
                <Label htmlFor="master-description">Description</Label>
                <Input
                  id="master-description"
                  name="description"
                  type="textarea"
                  rows="5"
                  maxLength="500"
                  placeholder={`Describe this ${label.toLowerCase()}`}
                  value={validation.values.description}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                />
                <div className="standard-drawer-form__count">
                  {validation.values.description.length} / 500
                </div>
              </FormGroup>
            </section>
          </div>
          <footer className="standard-drawer-form__actions">
            <Button
              className="standard-drawer-form__cancel"
              type="button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button className="standard-drawer-form__submit" type="submit">
              {isEditing ? "Save Changes" : `Create ${label}`}
            </Button>
          </footer>
        </Form>
      </OffcanvasBody>
    </Offcanvas>
  );
};

export default MasterList;
